import type { FC } from 'react';
import dp4 from './../../../resources/content/dp4.png';
import dp3 from './../../../resources/content/dp3.png';
import dp2 from './../../../resources/content/dp2.png';
import dp1 from './../../../resources/content/dp1.png';
import { WhatIsDPWrapper } from './WhatIsDP.styled';

interface WhatIsDPProps {}

const WhatIsDP: FC<WhatIsDPProps> = () => (
 <WhatIsDPWrapper>
    <header className="entry-header">
      <h1 className="entry-title" itemProp="headline">Introduction to Dynamic Programming</h1>			
   </header>
   <br/>
   <br/>
   <div className="entry-content clear" itemProp="text"  style={{height: 'auto !important'}}>
      <p className="text-align-justify">Why should you care about dynamic programming? The answer lies in efficiency. On one side, we have <b>*Dynamic Programming*</b> — a smart approach that significantly reduces redundant calculations. It optimizes both performance and feasibility by breaking down complex problems into simpler subproblems. On the other side, we have <b>*Brute Force*</b> methods, which can lead to exponential time complexity. This makes them impractical for larger problem instances because they involve exhaustive searching and lead to redundant calculations. </p>
      
      <figure className="aligncenter size-large">
        <img decoding="async" width="775" height="435" src={dp1} alt="" className="lazy-hidden" sizes="auto, (max-width: 775px) 100vw, 775px" />
      </figure>
      
      <h4 className="text-align-left">Dynamic Programming Cycle</h4>
      <p className="text-align-justify">A systematic approach that transforms complex problems into manageable solutions. </p>
      <ul className="text-align-justify">
        <li><strong>Break Down the Problem</strong></li>
        <ul className="text-align-justify">
          <li>We start by dividing our complex problem into smaller subproblems. This is like taking apart a complicated machine to understand each component.</li>
        </ul>
        <li><strong>Solve Subproblems</strong></li>
        <ul className="text-align-justify">
          <li>Next, we find solutions for each individual subproblem. These become our building blocks.</li>
        </ul>
        <li><strong>Store Results</strong></li>
        <ul className="text-align-justify">
          <li>Here's where the magic happens ~ we save our solutions to avoid recalculation. This is called memoization or tabulation.</li>
        </ul>
        <li><strong>Construct the Solution</strong></li>
        <ul className="text-align-justify">
          <li>We combine our subproblem solutions to solve the main problem efficiently.</li>
        </ul>
        <li><strong>Optimize Efficiency</strong></li>
        <ul className="text-align-justify">
          <li>Finally, we improve computational speed and resource usage, creating a streamlined solution.</li>
        </ul>
      </ul>

      <figure className="aligncenter size-large">
        <img decoding="async" width="775" height="435" src={dp2} alt="" className="lazy-hidden" sizes="auto, (max-width: 775px) 100vw, 775px" />
      </figure>
      

      <h4 className="text-align-left">Memoization</h4>
      <p className="text-align-justify">Imagine transforming an algorithm that takes hours to run into one that completes in seconds. That's the power of memoization! Think of it as a three-step optimization funnel that transforms how your programs handle repeated calculations.</p>
      <ul className="text-align-justify">
        <li><strong>Store Result</strong></li>
        <ul className="text-align-justify">
          <li>When your function calculates something for the first time, memoization caches that result. Think of it like writing down the answer to a math problem so you don't have to solve it again.</li>
        </ul>
        <li><strong>Reuse Result</strong></li>
        <ul className="text-align-justify">
          <li>The next time your function encounters the same input, instead of recalculating, it simply retrieves the cached result. This is where the magic happens!</li>
        </ul>
        <li><strong>Reduce Complexity</strong></li>
        <ul className="text-align-justify">
          <li>By eliminating redundant calculations, we dramatically decrease the time complexity of our algorithm. What might have been exponential time becomes polynomial or even linear!</li>
        </ul>
      </ul>

      <figure className="aligncenter size-large">
        <img decoding="async" width="775" height="435" src={dp3} alt="" className="lazy-hidden" sizes="auto, (max-width: 775px) 100vw, 775px" />
      </figure>

      <h4 className="text-align-left">Pros of Memoization</h4>
      <ul className="text-align-justify">
          <li><p className="pros"><b>Faster Execution:</b></p> This is the big one! By reusing cached results, we can speed up program performance dramatically.</li>
          <li><p className="pros"><b>Lower Complexity:</b></p> We're essentially trading space for time, making algorithms more efficient and scalable for larger inputs.</li>
          <li><p className="pros"><b>Recursive Efficiency:</b></p> Memoization is particularly powerful for recursive algorithms where the same subproblems appear multiple times.</li>
          <li><p className="pros"><b>Dynamic Programming:</b></p> It's essential for solving dynamic programming problems efficiently, opening up solutions to complex optimization challenges.</li>
          <li><p className="pros"><b>Language Support:</b></p> Most modern programming languages provide built-in support or libraries for memoization, making implementation straightforward.</li>
      </ul>

      <figure className="aligncenter size-large">
        <img decoding="async" width="775" height="435" src={dp4} alt="" className="lazy-hidden" sizes="auto, (max-width: 775px) 100vw, 775px" />
      </figure>

      <h4 className="text-align-left">The Trade offs</h4>
      <ul className="text-align-justify">
          <li><p className="cons"><b>Memory Usage:</b></p> We're storing results, which means increased memory consumption. For problems with many unique inputs, this can become significant.</li>
          <li><p className="cons"><b>Implementation Complexity:</b></p> You need to carefully manage your cache and avoid errors in your memoization logic.</li>
          <li><p className="cons"><b>Not Always Beneficial:</b></p> If function calls are infrequent or inputs rarely repeat, memoization might not provide meaningful performance gains.</li>
      </ul>
      

	

	

   </div>
 </WhatIsDPWrapper>
);

export default WhatIsDP;
